/**
 * Local-storage backed presentation preference for Spec's follow panel: is it
 * open, and how wide.
 *
 * Pure and dependency-free on purpose -- no React, no router, no runtime. The
 * panel and full page already share conversation, stream, approval, context,
 * and usage state through the one Spec runtime (`runtime-provider.tsx`); the
 * only thing this module owns is the sliver of state that is legitimately
 * local to a browser: whether the panel was left open, and how wide. It does
 * not decide WHICH conversation is open -- that stays the runtime's alone,
 * read through rather than duplicated (see `follow-provider.tsx`).
 */

/** The plan's own words: "bounded so the Parcel page remains usable." */
export const SPEC_PANEL_MIN_WIDTH = 360;
export const SPEC_PANEL_MAX_WIDTH = 520;
/** The band's midpoint: no narrower or wider bias than the other. */
export const SPEC_PANEL_DEFAULT_WIDTH = 440;

export interface SpecFollowPreference {
  open: boolean;
  width: number;
}

/** The DOM's own `Storage` shape, narrowed to what this module calls. */
export type SpecFollowStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function defaultSpecFollowPreference(): SpecFollowPreference {
  return { open: false, width: SPEC_PANEL_DEFAULT_WIDTH };
}

/**
 * `window.localStorage`, guarded.
 *
 * Null both where there is no `window` (this module's own test suite runs in
 * node) and where merely reading the property throws, which Safari does under
 * some private-browsing configurations. Callers get a value they can hand
 * straight to `readSpecFollowPreference` / `writeSpecFollowPreference` with no
 * second guard of their own.
 */
export function defaultSpecFollowStorage(): SpecFollowStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const STORAGE_KEY_PREFIX = 'parcel:spec-follow';

/**
 * Scoped to both the workspace and the member, per the plan ("Persist panel
 * open/width per member/workspace"). Null until both are known, which a
 * caller reads as "nothing to read or write yet" rather than as an
 * empty-string key that would quietly collide across members.
 */
export function specFollowStorageKey(
  workspaceId: string | null | undefined,
  memberId: string | null | undefined,
): string | null {
  const workspace = typeof workspaceId === 'string' ? workspaceId.trim() : '';
  const member = typeof memberId === 'string' ? memberId.trim() : '';
  if (!workspace || !member) return null;
  return `${STORAGE_KEY_PREFIX}:${workspace}:${member}`;
}

/**
 * Clamp to the band the plan requires ("Clamp desktop width to 360-520px"),
 * so main content can never be squeezed unusably thin no matter how a width
 * got into storage -- a hand-edited value, a future bug, or a value left over
 * from a narrower band than a later release ships.
 *
 * Anything that is not a finite number falls back to the default rather than
 * to a clamped edge: a value that was never a number (corrupt JSON, a numeric
 * string) is not "too wide" or "too narrow", it is simply not a measurement,
 * and the default is the more honest answer than pinning it to whichever edge
 * a naive coercion would land closest to.
 */
export function clampSpecPanelWidth(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return SPEC_PANEL_DEFAULT_WIDTH;
  }
  // Whole pixels only, so a pointer-drag handler cannot ratchet in fractions.
  const rounded = Math.round(value);
  return Math.min(SPEC_PANEL_MAX_WIDTH, Math.max(SPEC_PANEL_MIN_WIDTH, rounded));
}

export function readSpecFollowPreference(
  key: string | null,
  storage: SpecFollowStorage | null,
): SpecFollowPreference {
  if (!key || !storage) return defaultSpecFollowPreference();
  try {
    const raw = storage.getItem(key);
    if (!raw) return defaultSpecFollowPreference();
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return defaultSpecFollowPreference();
    }
    const record = parsed as Record<string, unknown>;
    return {
      open: record.open === true,
      width: clampSpecPanelWidth(record.width),
    };
  } catch {
    // Corrupt JSON, or a read that throws outright (Safari private mode).
    // Either way this is a rendering path, not a place to surface an error.
    return defaultSpecFollowPreference();
  }
}

export function writeSpecFollowPreference(
  key: string | null,
  preference: SpecFollowPreference,
  storage: SpecFollowStorage | null,
): void {
  if (!key || !storage) return;
  try {
    storage.setItem(
      key,
      JSON.stringify({
        open: preference.open === true,
        // Clamped on the way in, so a bad width -- a bug upstream, a value
        // hand-edited in devtools -- never reaches storage to be read back
        // uncontested by a future release that trusted it.
        width: clampSpecPanelWidth(preference.width),
      }),
    );
  } catch {
    // Safari private mode, or a full quota. Persisting a panel width is never
    // worth failing the caller over.
  }
}
